import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useActiveStore } from 'context/activeStoreProvider';

function ChevronDownIcon() {
    return (
        <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
    );
}

function StoreIcon() {
    return (
        <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 2.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg className="w-3.5 h-3.5 ml-auto shrink-0 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
    );
}

export default function StoreSwitcherDropdown() {
    const { activeStore, parentStore, childStores, activateStore, selectParentStore, isLoadingStore } = useActiveStore();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const router = useRouter();

    useEffect(() => {
        function handleOutsideClick(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    if (!parentStore) return null;

    const isParentActive = activeStore?.isParent === true;

    const handleSelectParent = () => {
        selectParentStore();
        setIsOpen(false);
        router.push('/reports');
    };

    const handleSelectChild = (store) => {
        activateStore(store.id);
        setIsOpen(false);
        router.push('/');
    };

    return (
        <div ref={containerRef} className="relative hide-on-mobile">
            <button
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-secondary-200 bg-white hover:bg-secondary-50 text-sm font-medium text-black-800 transition-colors duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 max-w-[200px]"
            >
                <StoreIcon />
                <span className="truncate flex-1 text-left text-xs">
                    {isLoadingStore ? 'Switching…' : (activeStore?.name || parentStore.name)}
                </span>
                <ChevronDownIcon />
            </button>

            {isOpen && (
                <ul
                    role="listbox"
                    aria-label="Store switcher"
                    className="absolute left-0 top-full mt-1.5 w-56 bg-white border border-secondary-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden"
                >
                    {/* Parent store — always first */}
                    <li role="option" aria-selected={isParentActive}>
                        <button
                            type="button"
                            onClick={handleSelectParent}
                            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors duration-100 cursor-pointer focus:outline-none focus-visible:bg-secondary-50
                                ${isParentActive ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-black-800 hover:bg-secondary-50'}`}
                        >
                            <StoreIcon />
                            <span className="truncate flex-1">{parentStore.name}</span>
                            <span className="text-xs text-gray-400 shrink-0">Main</span>
                            {isParentActive && <CheckIcon />}
                        </button>
                    </li>

                    {/* Divider + child stores */}
                    {childStores.length > 0 && (
                        <>
                            <li role="separator" className="my-1 border-t border-secondary-100" />
                            {childStores.map((store) => {
                                const isActive = !isParentActive && store.id === activeStore?.id;
                                return (
                                    <li key={store.id} role="option" aria-selected={isActive}>
                                        <button
                                            type="button"
                                            onClick={() => handleSelectChild(store)}
                                            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors duration-100 cursor-pointer focus:outline-none focus-visible:bg-secondary-50
                                                ${isActive ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-black-800 hover:bg-secondary-50'}`}
                                        >
                                            <StoreIcon />
                                            <span className="truncate">{store.name}</span>
                                            {isActive && <CheckIcon />}
                                        </button>
                                    </li>
                                );
                            })}
                        </>
                    )}
                </ul>
            )}
        </div>
    );
}
